import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { Code, Zap } from 'lucide-react'
import skillsService from '../../../services/skillsService'
import { useTheme } from '../../../contexts/ThemeContext'

const containerVariants = {
  offscreen: { opacity: 0 },
  onscreen: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 }
  }
};

const categoryVariants = {
  offscreen: { opacity: 0, y: 30, scale: 0.97 },
  onscreen: { 
    opacity: 1, y: 0, scale: 1,
    transition: { type: 'spring', stiffness: 200, damping: 24 }
  }
};

const tagVariants = {
  offscreen: { opacity: 0, scale: 0.7 },
  onscreen: { opacity: 1, scale: 1 }
};

export const SkillsSection = () => {
  const [skills, setSkills] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { isDark } = useTheme();

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        setLoading(true)
        setError(null)
        const [catRes, skillsRes] = await Promise.all([
          skillsService.getCategories(),
          skillsService.getSkills()
        ])

        const categories = catRes?.data || []
        const categoryIdToName = categories.reduce((acc, c) => {
          acc[c.id] = c.name || c.id
          return acc
        }, {})

        const skillsList = skillsRes?.data || []
        const grouped = skillsList.reduce((acc, s) => {
          if (s?.isActive === false) return acc
          const categoryName = categoryIdToName[s.category] || s.category || 'Uncategorized'
          if (!acc[categoryName]) acc[categoryName] = []
          acc[categoryName].push(s.name)
          return acc
        }, {})

        setSkills(grouped)
      } catch (err) {
        console.error('Failed to load skills', err)
        setError('Failed to load skills')
      } finally {
        setLoading(false)
      }
    }

    fetchSkills()
  }, [])

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
        <p>Loading skills...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-red-400">
        <p>{error}</p>
      </div>
    )
  }

  if (!Object.keys(skills).length) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
        <p>No skills data available</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 pb-16">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-10"
      >
        <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-neutral-100' : 'text-neutral-900'}`}>
          Technical Skills
        </h2>
        <p className={`text-sm ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>
          Technologies and tools I work with
        </p>
      </motion.div>

      {/* Skills Grid */}
      <motion.div 
        className="space-y-6"
        variants={containerVariants}
        initial="offscreen"
        whileInView="onscreen"
        viewport={{ once: true, margin: '-50px' }}
      >
        {Object.entries(skills).map(([category, skillList], index) => (
          <motion.div
            key={category}
            variants={categoryVariants}
            whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
            className={`p-5 rounded-xl transition-all duration-300 border-gradient ${
              isDark 
                ? 'glass-futuristic hover:shadow-lg hover:shadow-accent-500/10' 
                : 'glass-futuristic-light hover:shadow-lg hover:shadow-accent-500/15'
            }`}
          >
            {/* Category Header */}
            <div className="flex items-center gap-3 mb-4">
              <motion.div 
                className={`p-2 rounded-lg ${isDark ? 'bg-neutral-800' : 'bg-neutral-100'}`}
                whileHover={{ rotate: 360, transition: { duration: 0.6 } }}
              >
                <Code className="w-4 h-4 text-accent-500" />
              </motion.div>
              <h3 className={`font-semibold ${isDark ? 'text-neutral-100' : 'text-neutral-900'}`}>
                {category}
              </h3>
              <span className={`text-xs ml-auto ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
                {skillList.length}
              </span>
            </div>

            {/* Skills Tags */}
            <motion.div 
              className="flex flex-wrap gap-2"
              variants={{ onscreen: { transition: { staggerChildren: 0.03 } } }}
            >
              {skillList.map((skill, skillIndex) => (
                <motion.span
                  key={skillIndex}
                  variants={tagVariants}
                  whileHover={{ 
                    scale: 1.08, 
                    y: -2,
                    transition: { duration: 0.15 } 
                  }}
                  className={`px-2.5 py-1 rounded-md text-sm cursor-default transition-colors ${
                    isDark 
                      ? 'bg-neutral-800 text-neutral-300 hover:bg-accent-500/10 hover:text-accent-400' 
                      : 'bg-neutral-100 text-neutral-700 hover:bg-accent-50 hover:text-accent-600'
                  }`}
                >
                  {skill}
                </motion.span>
              ))}
            </motion.div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  )
}